import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { NoteConceptDetailResponse } from 'src/app/models/invoicing/note-concept-detail-response.mode';
import { NoteDetailModel } from 'src/app/models/invoicing/note-detail.model';
import { NoteModel } from 'src/app/models/invoicing/note.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { NoteDetailRegisterModel } from 'src/app/models/tables/invoicing/note-detail-register.model';
import { NoteConceptService } from 'src/app/services/invoicing/note-concept.service';
import { NoteDetailService } from 'src/app/services/invoicing/note-detail.service';
import { NoteService } from 'src/app/services/invoicing/note.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-note-detail',
  templateUrl: './note-detail.component.html',
  styleUrls: ['./note-detail.component.scss'],
  standalone: false,
})
export class NoteDetailComponent implements OnChanges {

  @Input() disabledActions = false;
  @Input() noteModel: NoteModel = new NoteModel();
  @Input() isDocumentSupport: boolean = false;
  @Input() headerChanged: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();
  @Output() updateNote = new EventEmitter<NoteModel>();

  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<NoteDetailRegisterModel> = new PagedResponseModel<NoteDetailRegisterModel>();
  detailHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  isFormView: boolean = false;
  noteConceptDetailResponse: NoteConceptDetailResponse = new NoteConceptDetailResponse();
  noteDetail: NoteDetailModel = new NoteDetailModel();
  noteId: number = 0;
  page: number = 1;
  quantityDetails: number = 0;
  tableActions: TableAction[] = [];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;

  constructor(private noteConceptService: NoteConceptService,
    private noteService: NoteService,
    private noteDetailService: NoteDetailService,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnChanges(changes: SimpleChanges) {
    const noteModel = changes["noteModel"];
    if (noteModel && !noteModel.firstChange) {
      if (this.noteModel.isLevelArticle) {
        await this.getColumnsForDetailsNote()
      } else {
        await this.getColumnsForConceptDetailsNote();
      }
      await this.getUpdateListDetail();
      this.noteDetail = new NoteDetailModel();
      this.noteConceptDetailResponse = new NoteConceptDetailResponse();
    }
    const disabledActions = changes["disabledActions"];
    if (disabledActions) {
      this.tableActions = this.disabledActions ? [] : this.getDefaultActions();
    }
  }

  async deleteNoteDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;
    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle la nota?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        if (this.noteModel.isLevelArticle) {
          await this.deleteNoteDetaills(selectedIds);
        } else {
          await this.deleteConceptDetails(selectedIds);
        }
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la nota fue eliminado exitosamente.'
        );
        this.noteDetail = new NoteDetailModel();
        this.clearSelectionTrigger++;

        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error en el proceso de eliminación:', error);
      }
    }
  }

  async deleteNoteDetaills(selectedIds: number[]): Promise<void> {
    try {
      await firstValueFrom(this.noteDetailService.deleteNoteDetail(selectedIds));
    } catch (error) {
      console.error('Error al eliminar el detalle de nota:', error);
      throw error;
    }
  }

  async deleteConceptDetails(selectedIds: number[]): Promise<void> {
    try {
      await firstValueFrom(this.noteConceptService.deleteSomeNoteConcepts(selectedIds));
    } catch (error) {
      console.error('Error al eliminar el detalle concepto de la nota:', error);
      throw error;
    }
  }

  async getColumnsForDetailsNote() {
    try {
      this.detailHeader = await firstValueFrom(this.noteDetailService.getColumnsForDetailsNote(this.isDocumentSupport));
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general:', error);
    }
  }

  async getColumnsForConceptDetailsNote() {
    try {
      this.detailHeader = await firstValueFrom(this.noteConceptService.GetColumnsForConceptDetailsNoteAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general:', error);
    }
  }

  async getConceptDetailsNoteWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.noteConceptService.getNotesConceptDetailsByNoteIdWithPagination(this.page, this.limit, this.columnSort, this.ascendant,
        this.noteModel.noteId));
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.quantityDetails);
    } catch (error) {
      console.error('Error al buscar los detalles de las notas por concepto.', error);
    }
  }

  async getDetailsNoteWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.noteDetailService.getNotesDetailsByNoteIdWithPagination(this.page, this.limit, this.columnSort, this.ascendant,
        this.noteModel.noteId, this.isDocumentSupport));
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.quantityDetails);
    } catch (error) {
      console.error('Error al buscar los detalles de las notas.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      }
    ];
  }

  getFormDetailView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getNote(note: NoteModel) {
    this.updateNote.emit(note);
  }

  async getNoteConceptByKeys(noteId: number, conceptInvoicingId: number, storeId: number) {
    if (conceptInvoicingId != 0 && storeId != 0) {
      try {
        this.noteConceptDetailResponse = await firstValueFrom(this.noteConceptService.getNoteConceptByKeys(noteId, conceptInvoicingId, storeId));
      } catch (error) {
        console.error('Error al buscar la nota concepto por claves en registro nota detalle.', error);
      }
    }
  }

  getNoteConceptDetailResponse(noteConceptDetailResponse: NoteConceptDetailResponse) {
    this.noteConceptDetailResponse = noteConceptDetailResponse;
  }

  getNoteDetail(noteDetail: NoteDetailModel) {
    this.noteDetail = noteDetail;
  }

  async getNoteDetailById(noteDetailId: number) {
    try {
      this.noteDetail = await firstValueFrom(this.noteDetailService.getNoteDetailById(noteDetailId));
    } catch (error) {
      console.error('Error al buscar la nota detalle por Id.', error);
    }
  }

  async getUpdateListDetail() {
    if (this.noteModel.isLevelArticle) {
      await this.getDetailsNoteWithPagination()
    } else {
      await this.getConceptDetailsNoteWithPagination();
    }
  }

  async getUpdateListDetailByDelete() {
    this.noteDetail = new NoteDetailModel();
    this.getNote(await firstValueFrom(this.noteService.getNoteById(this.noteModel.noteId)));

    this.clearSelectionTrigger++;
    this.getUpdateListDetail();
  }

  async onAction(event: { action: string, item?: NoteDetailRegisterModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (this.noteModel.isLevelArticle) {
        this.getNoteDetailById(event.item.noteDetailId);
      } else {
        this.getNoteConceptByKeys(event.item.noteId, event.item.conceptInvoicingId, event.item.storeId);
      }
      this.getFormDetailView(true);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteNoteDetail(event.selectedItems);
    }
  }

  openRegister() {
    if (this.noteModel.noteId == 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la nota.');
      return;
    } else {
      this.isFormView = true;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getDetailsNoteWithPagination();
  }
}
